import React from 'react';
import { X, Shield, CheckCircle2, Circle, AlertCircle } from 'lucide-react';
import { useCareProfiles } from '../../hooks/useCareProfiles';
import { useDietaryFilter } from '../../contexts/DietaryFilterContext';

// ─────────────────────────────────────────────────────────────────────────────
// DietaryFilterPanel
//
// Reads iOS CareProfile fields:
//   profile.allergies           → allergen count display
//   profile.dietaryRestrictions → restriction count display
//   profile.tags                → diet style tags
// ─────────────────────────────────────────────────────────────────────────────

const DietaryFilterPanel = ({ onClose }) => {
  const { careProfiles, loading } = useCareProfiles();
  const { selectedProfileIds, toggleProfile, selectAllProfiles, clearAllProfiles } = useDietaryFilter();

  if (loading) {
    return (
      <div className="mb-lg p-lg rounded-3xl bg-white/80 backdrop-blur-sm border border-primary/10 shadow-sm">
        <p className="text-center text-text-secondary">Loading profiles...</p>
      </div>
    );
  }

  const isProfileSelected = (profileId) => selectedProfileIds.includes(profileId);
  const hasAnySelected = selectedProfileIds.length > 0;

  // Non-admin profiles only — mirrors iOS displayProfiles
  const displayProfiles = careProfiles.filter(p => !p.isAdmin);

  return (
    <div className="mb-lg rounded-3xl bg-white/80 backdrop-blur-sm border border-primary/10 shadow-lg overflow-hidden">

      {/* Header */}
      <div className="p-lg border-b border-primary/10">
        <div className="flex items-center justify-between mb-sm">
          <div className="flex items-center gap-sm">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/20 to-accent/10 flex items-center justify-center">
              <Shield size={20} className="text-primary" />
            </div>
            <div>
              <h3 className="text-title-3 text-primary font-semibold">Dietary Filters</h3>
              <p className="text-caption text-text-tertiary">
                {hasAnySelected
                  ? `${selectedProfileIds.length} profile${selectedProfileIds.length !== 1 ? 's' : ''} active`
                  : 'No filters active'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-sm rounded-full hover:bg-text-tertiary/10 transition-colors"
          >
            <X size={20} className="text-text-secondary" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-lg">

        {/* Quick actions */}
        {displayProfiles.length > 0 && (
          <div className="flex gap-sm mb-lg">
            <button
              onClick={selectAllProfiles}
              disabled={selectedProfileIds.length === displayProfiles.length}
              className="flex-1 py-sm px-md rounded-xl bg-primary/5 hover:bg-primary/10 border border-primary/20 text-caption font-semibold text-primary transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Enable All
            </button>
            <button
              onClick={clearAllProfiles}
              disabled={selectedProfileIds.length === 0}
              className="flex-1 py-sm px-md rounded-xl bg-surface hover:bg-text-tertiary/5 border border-text-tertiary/10 text-caption font-semibold text-text-secondary transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Disable All
            </button>
          </div>
        )}

        {/* Profile list */}
        {displayProfiles.length > 0 ? (
          <div className="space-y-sm">
            {displayProfiles.map(profile => {
              const selected = isProfileSelected(profile.id);

              // ✅ Correct iOS field names
              const allergyCount     = (profile.allergies           || []).length;
              const restrictionCount = (profile.dietaryRestrictions || []).length;
              const tags             = (profile.tags || []).map(t =>
                typeof t === 'string' ? t : (t?.dietaryTag || t?.rawValue || '')
              ).filter(Boolean);
              const firstTag = tags[0] || null;

              return (
                <button
                  key={profile.id}
                  onClick={() => toggleProfile(profile.id)}
                  className={`w-full group relative overflow-hidden rounded-2xl transition-all text-left ${
                    selected
                      ? 'bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20'
                      : 'bg-surface hover:bg-primary/5 border border-transparent'
                  }`}
                >
                  <div className="p-md flex items-center gap-md">

                    {/* Avatar */}
                    <div className={`relative w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-lg transition-all ${
                      selected ? 'bg-primary/20 text-primary' : 'bg-primary/10 text-primary group-hover:bg-primary/15'
                    }`}>
                      {profile.profileImageURL ? (
                        <img
                          src={profile.profileImageURL}
                          alt={profile.name}
                          className="w-full h-full rounded-full object-cover"
                        />
                      ) : (
                        (profile.name || '?').charAt(0).toUpperCase()
                      )}
                      {selected && (
                        <div className="absolute -top-1 -right-1 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                          <CheckCircle2 size={12} className="text-white" />
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-sm mb-xs flex-wrap">
                        <p className="text-callout font-semibold text-primary">
                          {profile.name}
                        </p>
                        <span className="px-sm py-xs bg-primary/10 rounded-full text-caption text-primary font-medium capitalize">
                          {profile.relationship}
                        </span>
                      </div>

                      <div className="flex flex-wrap gap-x-md gap-y-xs text-caption text-text-secondary">
                        {firstTag && (
                          <span className="inline-flex items-center gap-xs">
                            <Circle size={4} className="fill-current" />
                            <span className="capitalize">{firstTag}</span>
                          </span>
                        )}
                        {allergyCount > 0 && (
                          <span className="inline-flex items-center gap-xs text-error">
                            <AlertCircle size={11} />
                            {allergyCount} allergen{allergyCount !== 1 ? 's' : ''}
                          </span>
                        )}
                        {restrictionCount > 0 && (
                          <span className="inline-flex items-center gap-xs text-text-secondary">
                            <Circle size={4} className="fill-current" />
                            {restrictionCount} restriction{restrictionCount !== 1 ? 's' : ''}
                          </span>
                        )}
                        {allergyCount === 0 && restrictionCount === 0 && !firstTag && (
                          <span className="text-text-tertiary italic">No restrictions set</span>
                        )}
                      </div>
                    </div>

                    {/* Checkbox */}
                    <div className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                      selected
                        ? 'border-primary bg-primary'
                        : 'border-text-tertiary/30 group-hover:border-primary/50'
                    }`}>
                      {selected && <CheckCircle2 size={14} className="text-white" />}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-xl">
            <div className="w-16 h-16 rounded-full bg-primary/5 flex items-center justify-center mx-auto mb-md">
              <Shield size={32} className="text-primary/40" />
            </div>
            <p className="text-callout text-text-secondary mb-sm font-semibold">
              No care profiles found
            </p>
            <p className="text-caption text-text-tertiary">
              Add care profiles in the Care Hub to filter products by dietary needs
            </p>
          </div>
        )}
      </div>

      {/* Footer */}
      {hasAnySelected && (
        <div className="px-lg py-md bg-primary/5 border-t border-primary/10">
          <div className="flex items-start gap-sm">
            <Shield size={14} className="text-primary flex-shrink-0 mt-xs" />
            <p className="text-caption text-text-secondary">
              Products containing allergens or restricted ingredients from{' '}
              <strong className="text-primary">any selected profile</strong> are hidden.
            </p>
          </div>
        </div>
      )}

      {!hasAnySelected && displayProfiles.length > 0 && (
        <div className="px-lg py-md bg-surface border-t border-text-tertiary/10">
          <div className="flex items-start gap-sm">
            <AlertCircle size={14} className="text-text-tertiary flex-shrink-0 mt-xs" />
            <p className="text-caption text-text-tertiary">
              All dietary filters are disabled. Showing all products.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default DietaryFilterPanel;